<script lang="ts" setup>
import axios from "axios";
import { computed, inject, PropType, ref } from "vue";
import { IAjaxResponse } from "../Ajax";
import { SITEURL } from "@libTs/cms.globals";
import { TranslationService } from "../services/translation.service";
import { vInputText as vInputTextDir } from "../directives/v-input-text";

const vInputText = vInputTextDir;

interface ISetPWScope {
    token: string;
    action: string;
    email: string;
    password: string;
    password2: string;
}

enum EAction {
    RESET = "reset",
    NEW = "new",
}

type sendingStatus =
    | "ok"
    | "error"
    | "set-new-pw-mail"
    | "use-reset-pw"
    | number;
const sendingStatus = ref<sendingStatus | null>(null);

const t = inject<TranslationService>("translationSvc")!.t;
const isSending = ref(false);
const sendingSuccessful = ref(false);
const sendData = ref<ISetPWScope>({
    token: "",
    action: "",
    email: "",
    password: "",
    password2: "",
});

const { username, token, action } = defineProps({
    username: {
        type: String,
        required: true,
    },
    token: {
        type: String,
        required: true,
    },
    action: {
        type: String as PropType<EAction>,
        required: true,
    },
});

Object.assign(sendData.value, {
    email: username,
    token: token,
    action: action,
});

const isPristine = computed(() => {
    return (
        sendData.value.email === "" ||
        sendData.value.password === "" ||
        sendData.value.password2 === ""
    );
});

const isInavlid = computed(() => {
    return (
        isPristine.value || sendData.value.password !== sendData.value.password2
    );
});

const sendForm = (): void => {
    if (isInavlid.value || sendingSuccessful.value) {
        return;
    }

    sendingSuccessful.value = false;
    isSending.value = true;

    axios
        .post<IAjaxResponse>(
            SITEURL + "actions/sapShop/set-new-pw-with-timeout/set-password",
            sendData.value,
            {
                responseType: "json",
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            },
        )
        .then(
            // success
            (response) => {
                const data = response.data;
                if (data && data.sent) {
                    sendingSuccessful.value = true;
                    window.location.href = SITEURL;

                    console.info("Password reset successful.");
                } else {
                    sendingSuccessful.value = false;
                    sendingStatus.value = data.status;

                    console.warn(
                        "Error " +
                            data.status +
                            ": Wrong or invalid data, new password has not set. ",
                    );
                }
            },

            // error
            (response) => {
                sendingStatus.value = 500;

                console.warn("Error: No response from server.");
                console.info(response);
            },
        )
        .finally(() => {
            isSending.value = false;
        });
};
</script>

<template>
    <div class="col-lg-9 col-lg-offset-2">
        <form
            name="form"
            :style="sendingSuccessful ? { display: 'none' } : {}"
            @submit.prevent="sendForm()"
            novalidate
        >
            <slot></slot>

            <div class="mt-5">
                <label class="text-f7">{{ t("EMAIL") }}</label>

                <input
                    class="!px-5 !py-3 w-full bg-grey-50 rounded-s input-text-input mt-2"
                    type="email"
                    name="username"
                    :value="sendData.email"
                    :placeholder="t('EMAIL')"
                    required
                    disabled
                />
            </div>
            <div class="mt-5">
                <label class="text-f7">{{ t("PASSWORD") }}</label>

                <input
                    class="!px-5 !py-3 w-full bg-grey-50 rounded-s input-text-input mt-2"
                    type="password"
                    name="password"
                    v-model="sendData.password"
                    :placeholder="t('PASSWORD')"
                    required
                />
            </div>
            <div class="mt-5" style="position: relative">
                <label class="text-f7">{{ t("REPEAT_PASSWORD") }}</label>

                <input
                    class="!px-5 !py-3 w-full bg-grey-50 rounded-s input-text-input mt-2"
                    type="password"
                    name="password-confirmation"
                    v-model="sendData.password2"
                    :placeholder="t('REPEAT_PASSWORD')"
                    required
                />
            </div>

            <div
                class="mt-10"
                :class="{ 'loading-animation': isSending }"
                style="display: inline-block"
            >
                <input
                    type="submit"
                    class="submit btn-cb"
                    :class="isSending ? 'loading-animation' : ''"
                    :disabled="
                        sendData.password != sendData.password2 ||
                        isPristine ||
                        isInavlid ||
                        sendingSuccessful ||
                        isSending
                    "
                    value="Save new password"
                />
            </div>

            <div
                class="mt-10 text-red"
                :style="
                    !sendingStatus || sendingStatus == 200
                        ? { display: 'none' }
                        : {}
                "
            >
                <p>{{ t("CONNECTION_ERROR") }}</p>
            </div>
        </form>

        <!-- sending successful message -->
        <div v-if="sendingSuccessful">
            <div class="mt-10">
                <br />
                <div class="h2">
                    {{
                        sendData.action === EAction.RESET
                            ? t("PASSWORD_UPDATED")
                            : t("SET_PASSWORD_SUCCESS")
                    }}
                </div>
                <br />
                <p>
                    <a class="btn-cb" href="{{ siteUrl }}">{{
                        t("BACK_TO_HOME")
                    }}</a>
                </p>
            </div>
        </div>
    </div>
</template>
